import { useState } from "react";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import AutopilotSection from "@/components/AutopilotSection";
import BrandsMarquee from "@/components/BrandsMarquee";
import CtaBanner from "@/components/CtaBanner";
import FaqSection from "@/components/FaqSection";
import FeaturesSection, { type Feature } from "@/components/FeaturesSection";
import Footer from "@/components/Footer";
import GallerySection from "@/components/GallerySection";
import HeroSection from "@/components/HeroSection";
import HowItWorks from "@/components/HowItWorks";
import ModelsShowcase from "@/components/ModelsShowcase";
import Navbar from "@/components/Navbar";
import SocialProof from "@/components/SocialProof";

import captionVideosPreviewWebp from "@/assets/optimized/preview-caption-videos.webp";
import upscaleVideoPreviewWebp from "@/assets/optimized/preview-upscale-video.webp";
import backgroundReplacerPreview from "@/assets/preview-background-replacer.webp";
import captionVideosPreview from "@/assets/preview-caption-videos.webp";
import editWithAiPreview from "@/assets/preview-edit-with-ai.webp";
import flatLayPreview from "@/assets/preview-flat-lay.webp";
import ghostMannequinPreview from "@/assets/preview-ghost-mannequin.webp";
import removeBackgroundPreview from "@/assets/preview-remove-background.webp";
import resizePreview from "@/assets/preview-resize.webp";
import upscaleVideoPreview from "@/assets/preview-upscale-video.webp";
import upscalePreview from "@/assets/preview-upscale.webp";
import virtualTryonPreview from "@/assets/preview-virtual-tryon.webp";
import PageSEO from "@/components/PageSEO";
import homeMd from "@/content/faqs/home.md?raw";
import { parseFaqs } from "@/lib/faqs";
import { buildHomeJsonLd } from "@/lib/seo";
import { toolAppHref } from "@/lib/tools";
const FASHION_CATALOGUE_CDN =
    "https://cdn.pixro.ai/ab97372c-faca-4d0b-bb01-6bfdefb4ee23.jpg";
const CGI_VIDEOS_CDN =
    "https://cdn.pixro.ai/dab06e54-2c2b-4365-b327-29b962b365eb.mp4";
const FASHION_REELS_CDN =
    "https://cdn.pixro.ai/a9899ba8-1027-4449-ab7b-82cd06847194.mp4";
const TALKING_AVATARS_CDN =
    "https://cdn.pixro.ai/93523cad-2bdb-447e-8dd7-6150c44a0af4.mp4";
const UGC_VIDEOS_CDN =
    "https://cdn.pixro.ai/4b505bf2-046e-44f7-9701-474701404285.mp4";
const PRODUCT_VIDEOS_CDN =
    "https://cdn.pixro.ai/fc50fa4b-b4bd-411b-b2b0-f95e70e37d76.mp4";

const homeFaqs = parseFaqs(homeMd);

const homeTools: Feature[] = [
    // ── Live tools ────────────────────────────────────────────────────────
    {
        title: "Fashion Catalogue",
        desc: "Generate a complete catalogue across multiple poses",
        src: FASHION_CATALOGUE_CDN,
        type: "image",
        href: toolAppHref("Fashion Catalogue"),
    },
    {
        title: "Product videos",
        desc: "Turn product photos into stunning AI videos",
        src: PRODUCT_VIDEOS_CDN,
        type: "video",
        href: toolAppHref("Product videos"),
    },
    {
        title: "UGC videos",
        desc: "Authentic creator-style videos at scale",
        src: UGC_VIDEOS_CDN,
        type: "video",
        href: toolAppHref("UGC videos"),
    },
    {
        title: "AI Fashion reels",
        desc: "AI-crafted reels that captivate audiences",
        src: FASHION_REELS_CDN,
        type: "video",
        href: toolAppHref("AI Fashion reels"),
    },
    {
        title: "CGI videos",
        desc: "Cinematic CGI-style ads in a few clicks",
        src: CGI_VIDEOS_CDN,
        type: "video",
        href: toolAppHref("CGI videos"),
    },
    {
        title: "Ghost mannequin",
        desc: "Invisible mannequin effects for market-ready visuals",
        src: ghostMannequinPreview,
        type: "image",
        href: toolAppHref("Ghost mannequin"),
    },
    {
        title: "Flat-lay",
        desc: "Design flat-lay clothing photos with AI",
        src: flatLayPreview,
        type: "image",
        href: toolAppHref("Flat-lay"),
    },
    {
        title: "Background replacer",
        desc: "Swap backgrounds with realistic AI scenes",
        src: backgroundReplacerPreview,
        type: "image",
        href: toolAppHref("Background replacer"),
    },
    {
        title: "Upscale",
        desc: "Sharpen and enhance image resolution with AI",
        src: upscalePreview,
        type: "image",
        href: toolAppHref("Upscale"),
    },
    {
        title: "Resize",
        desc: "Resize images for any platform or aspect ratio",
        src: resizePreview,
        type: "image",
        href: toolAppHref("Resize"),
    },
    {
        title: "Remove background",
        desc: "Remove backgrounds and isolate products instantly",
        src: removeBackgroundPreview,
        type: "image",
        href: toolAppHref("Remove background"),
    },
    {
        title: "Edit with AI",
        desc: "Edit product images with AI-powered tools",
        src: editWithAiPreview,
        type: "image",
        href: toolAppHref("Edit with AI"),
    },
    {
        title: "Virtual Try-On",
        desc: "Try fashion garments on AI models instantly",
        src: virtualTryonPreview,
        type: "image",
        href: toolAppHref("Virtual Try-On"),
    },
    // ── Coming soon ───────────────────────────────────────────────────────
    {
        title: "Talking avatars",
        desc: "Lifelike avatars that speak your script",
        src: TALKING_AVATARS_CDN,
        type: "video",
        href: toolAppHref("Talking avatars"),
        comingSoon: true,
    },
    {
        title: "Upscale video",
        desc: "Boost resolution up to 4K with crisp detail",
        src: upscaleVideoPreview,
        webpSrc: upscaleVideoPreviewWebp,
        type: "image",
        href: toolAppHref("Upscale video"),
        comingSoon: true,
    },
    {
        title: "Caption videos",
        desc: "Auto-generate stylish captions for any video",
        src: captionVideosPreview,
        webpSrc: captionVideosPreviewWebp,
        type: "image",
        href: toolAppHref("Caption videos"),
        comingSoon: true,
    },
];

const Index = () => {
    const [bannerVisible, setBannerVisible] = useState(true);

    return (
        <>
            <PageSEO
                title="Your Always-On AI Creative Team | Pixro AI"
                description="Generate high-converting product photos, AI fashion models, UGC videos, and ad creatives in seconds. The AI creative platform for D2C and ecommerce brands."
                path="/"
                jsonLd={buildHomeJsonLd(homeFaqs)}
            />
            <div className="min-h-screen bg-background">
                {bannerVisible && (
                    <AnnouncementBanner
                        onDismiss={() => setBannerVisible(false)}
                    />
                )}
                <Navbar offsetTop={bannerVisible} />
                <main>
                    {bannerVisible && <div className="h-10" />}
                    <HeroSection />
                    <HowItWorks />
                    <BrandsMarquee />
                    <FeaturesSection
                        items={homeTools}
                        heading="Everything your creative team does, on autopilot."
                        subheading="From photoshoots to videos and ads, your AI crew generates high-converting, conversion-ready creatives in seconds."
                    />
                    <AutopilotSection />
                    <SocialProof />
                    <ModelsShowcase />
                    <CtaBanner />
                    <GallerySection />
                    {/* <ReadySection /> */}
                    <FaqSection />
                </main>
                <Footer />
            </div>
        </>
    );
};

export default Index;
